<script setup lang="ts">
import type { Index } from "meilisearch"
import type { GlobalSearchResult, GlobalSearchSelectOpts } from "~/utils/globalsearch"
import { useStorage } from "@vueuse/core"

const { t } = useI18n()

const props = withDefaults(
  defineProps<{
    index: Index
    showEsc?: boolean
    typeNames: string[]
    searchPlaceholder?: string
    cleanSearch?: boolean
    filter?: string
  }>(),
  { showEsc: false, cleanSearch: false },
)

const selected = defineModel<GlobalSearchResult | undefined>("selected", {
  required: true,
})

const selectedOpts = defineModel<GlobalSearchSelectOpts>("selectedOpts", {
  required: false,
  default: () => ({ isMeta: false }),
})

// types whose records the user's scopes give access to: the others always come back empty
const visibleTypeNames = computed(() => props.typeNames.filter(isGlobalSearchTypeAllowed))

// lists the main types the user can search, e.g. "Search for sites, invoices, ..."
const defaultPlaceholder = computed(() => {
  const types = ["site", "contract", "invoice"].filter((type) =>
    visibleTypeNames.value.includes(type),
  )

  return t("placeholder", { types: types.map((type) => t(`placeholder_type.${type}`)).join(", ") })
})

const { onSearchError } = useMeilisearchTokenRefresh(() => props.index)
const latestSearch = useLatestSearch()

// internal states
// all types are selected by default, persisted across sessions
const selectedTypes = useStorage<string[]>(StorageKeys.GlobalSearchSelectedTypes, [
  ...visibleTypeNames.value,
])

// stored selection is shared with usages that accept different typeNames:
// drop types unavailable here, and fall back to all selected if none remain
const availableTypes = selectedTypes.value.filter((t) => visibleTypeNames.value.includes(t))
selectedTypes.value = availableTypes.length > 0 ? availableTypes : [...visibleTypeNames.value]
const searchResults = ref<GlobalSearchResult[]>([])
const selectedIndex = ref(0)

const emits = defineEmits<{
  close: []
}>()

const searchInputRef = ref<any | null>(null)
const resultsContainerRef = ref<HTMLDivElement | null>(null)

const search = useStorage(StorageKeys.GlobalSearch, "")

onMounted(() => {
  // select all text on mount
  nextTick(() => {
    searchInputRef.value?.input.select()

    if (props.cleanSearch) {
      search.value = ""
    }
  })
})

const isLoading = ref(false)

function toggleType(type: string) {
  if (selectedTypes.value.includes(type)) {
    selectedTypes.value = selectedTypes.value.filter((t) => t !== type)
  } else {
    selectedTypes.value.push(type)
  }
}

function doubleClickToggleType(type: string) {
  if (selectedTypes.value.length === 1 && selectedTypes.value.includes(type)) {
    selectedTypes.value = [...visibleTypeNames.value]
  } else {
    selectedTypes.value = [type]
  }
}

// Identifies the inputs the displayed results were fetched for: results are stale while the
// debounced fetch is pending or in flight, e.g. right after pasting over the previous query.
const searchKey = computed(() => JSON.stringify([search.value, selectedTypes.value, props.filter]))
const resultsKey = ref<string>()
const hasStaleResults = computed(() => searchKey.value !== resultsKey.value)

// Enter pressed on stale results is deferred until the results of the current input arrive
const isConfirmPending = ref(false)

async function fetchResults() {
  // Only the latest search may write results: a newer one aborts this signal
  const signal = latestSearch.start()
  const key = searchKey.value
  const query = search.value
  let results: GlobalSearchResult[] = []

  // without any visible type, the type filter below would be empty and match every record
  if (query.length > 2 && visibleTypeNames.value.length > 0) {
    isLoading.value = true

    // Construct type filters (OR'd together)
    const typeFilters = selectedTypes.value.map((type) => `type=${type}`)

    // Combine with prop filter if provided (AND'd)
    let filter = typeFilters.join(" OR ")
    if (R.isNonNullish(props.filter)) {
      filter = `(${filter}) AND (${props.filter})`
    }

    try {
      const raw = await props.index.search(
        query,
        {
          sort: ["start:desc"],
          // only the `_formatted` fields rendered by GlobalSearchRow: highlighting costs search time
          attributesToHighlight: [
            "name",
            "sites.name",
            "purchase_order",
            "contract.purchase_order",
          ],
          // feeds GlobalSearchMatchBadgeCloud
          showMatchesPosition: true,
          filter,
        },
        { signal },
      )
      results = (raw?.hits || []) as unknown as GlobalSearchResult[]
    } catch (error) {
      if (signal.aborted) return

      await onSearchError(error, key, fetchResults)
    }
  }

  if (signal.aborted) return

  isLoading.value = false
  searchResults.value = results
  resultsKey.value = key
  selectedIndex.value = 0

  if (isConfirmPending.value && !hasStaleResults.value) {
    isConfirmPending.value = false
    confirmSelection(true)
  }
}

watchDebounced(() => [search.value, selectedTypes.value, props.filter], fetchResults, {
  debounce: 150,
  maxWait: 400,
  deep: true,
  immediate: true,
})

function close() {
  latestSearch.abort()
  isConfirmPending.value = false
  emits("close")
  search.value = ""
}

function selectNext() {
  if (searchResults.value.length > 0) {
    selectedIndex.value = (selectedIndex.value + 1) % searchResults.value.length
    scrollSelectedIntoView()
  }
}

function selectPrevious() {
  if (searchResults.value.length > 0) {
    selectedIndex.value =
      (selectedIndex.value - 1 + searchResults.value.length) % searchResults.value.length
    scrollSelectedIntoView()
  }
}

function scrollSelectedIntoView() {
  nextTick(() => {
    const selectedElement = resultsContainerRef.value?.querySelector(
      `[data-index="${selectedIndex.value}"]`,
    )
    selectedElement?.scrollIntoView({ block: "nearest" })
  })
}

function handleSelect(event: MouseEvent, index: number) {
  selectedOpts.value.isMeta = Boolean(event.metaKey)
  selectedIndex.value = index
  confirmSelection(!selectedOpts.value.isMeta)
}

function confirmSelection(withClose = true) {
  if (selectedIndex.value >= 0 && searchResults.value.length > 0) {
    selected.value = searchResults.value[selectedIndex.value]
    if (withClose) {
      close()
    }
  }
}

// Always focus on input when modal is opened
onMounted(() => {
  const handleMouseDown = (e: MouseEvent) => {
    if (!searchInputRef.value?.$el.contains(e.target as Node)) {
      e.preventDefault()
      searchInputRef.value.input?.focus()
    }
  }

  document.addEventListener("mousedown", handleMouseDown)

  onBeforeUnmount(() => {
    document.removeEventListener("mousedown", handleMouseDown)
  })
})

const hasMouseMoved = ref(false)
onMounted(() => {
  const onMouseMove = () => {
    hasMouseMoved.value = true
    window.removeEventListener("mousemove", onMouseMove)
  }
  window.addEventListener("mousemove", onMouseMove)
})

// Keyboard event handler
function handleKeydown(event: KeyboardEvent) {
  switch (event.key) {
    case "ArrowDown":
      event.preventDefault()
      selectNext()
      break
    case "ArrowUp":
      event.preventDefault()
      selectPrevious()
      break
    case "Tab":
      if (event.shiftKey) {
        event.preventDefault()
        selectPrevious()
        break
      }
      event.preventDefault()
      selectNext()
      break
    case "n":
      if (event.ctrlKey) {
        event.preventDefault()
        selectNext()
      }
      break
    case "p":
      if (event.ctrlKey) {
        event.preventDefault()
        selectPrevious()
      }
      break
    case "Enter":
      event.preventDefault()
      if (hasStaleResults.value) {
        isConfirmPending.value = true
      } else {
        confirmSelection(true)
      }
      break
    case "Escape":
      event.preventDefault()
      close()
      break
  }
}

const uiInput = /* ui */ {
  icon: {
    leading: {
      wrapper: "absolute inset-y-0 start-0 flex items-center mt-2",
    },
  },
}
</script>

<template>
  <div class="w-full">
    <!-- Search Input -->
    <div
      class="relative flex flex-auto flex-row items-center gap-x-4 border-b border-rnxblue-200 px-4 pb-1 pt-4"
    >
      <UInput
        ref="searchInputRef"
        v-model="search"
        :placeholder="searchPlaceholder ?? defaultPlaceholder"
        variant="none"
        color="rnxblue"
        :ui="uiInput"
        size="lg"
        class="w-full flex-auto"
        :icon="$icons.search"
        :trailing-icon="undefined"
        type="search"
        :loading="false"
        autofocus
        @keydown="handleKeydown"
      />
      <div class="inline-flex shrink-0 items-center gap-x-2">
        <button
          v-for="type in visibleTypeNames"
          :key="type"
          v-tippy="$t(`globalsearch.type.${type}`)"
          type="button"
          :class="[
            selectedTypes.includes(type)
              ? 'opacity-70 ring-rnxblue-100'
              : 'text-gray-300 opacity-50 ring-gray-300',
            selectedTypes.includes(type) && type in $icons.model
              ? mapModelIconColor[type as keyof typeof mapModelIconColor] || 'text-rnxblue-400'
              : '',
          ]"
          class="flex size-6 cursor-pointer flex-row items-center justify-center truncate rounded-full ring-1 ring-inset"
          @click="() => toggleType(type)"
          @dblclick="() => doubleClickToggleType(type)"
        >
          <UIcon
            class="size-3"
            :name="$icons.model[type as keyof typeof mapGlobSearchTypeIcons] ?? $icons.warning"
          />
        </button>
      </div>
      <div class="inline-flex shrink-0 items-center gap-x-2">
        <GlobalSearchShortcutTooltip />
        <UKbd v-if="showEsc" @click="close">{{ t("esc") }}</UKbd>
      </div>
    </div>

    <!-- Results Area -->
    <div>
      <div
        ref="resultsContainerRef"
        class="max-h-[400px] overflow-y-auto transition-all duration-200 ease-in-out"
      >
        <!-- Loading State -->
        <div v-if="isLoading && !searchResults.length" class="py-10 text-center">
          <UIcon :name="$icons.refresh" class="mx-auto mb-2 size-8 animate-spin text-gray-400" />
          <p class="text-gray-500 dark:text-gray-400">{{ t("loading") }}</p>
        </div>

        <!-- Initial State -->
        <div v-else-if="!searchResults.length" class="py-10 text-center"></div>

        <!-- Results List -->
        <div
          v-else
          class="m-2 flex flex-col gap-y-1"
          :class="{ 'pointer-events-none': !hasMouseMoved }"
        >
          <div
            v-for="(item, idx) in searchResults"
            :key="item.uuid"
            :data-index="idx"
            :class="[
              'cursor-pointer rounded-lg p-3 transition-colors',
              selectedIndex === idx
                ? 'bg-gray-100 dark:bg-gray-800'
                : 'hover:bg-gray-100 dark:hover:bg-gray-800',
            ]"
            @click="(event) => handleSelect(event, idx)"
          >
            <div class="flex items-start">
              <GlobalSearchRow :item :search />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<i18n lang="yaml">
en:
  loading: Searching...
  esc: Escape
  placeholder: Search for {types}, ...
  placeholder_type:
    site: sites
    contract: contracts
    invoice: invoices
fr-FR:
  loading: Recherche...
  esc: Echap
  placeholder: Chercher {types}, ...
  placeholder_type:
    site: un site
    contract: un contrat
    invoice: une facture
</i18n>
